import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { Dictionary } from 'lodash';
import { Currency } from 'api/account';
import { FileAttachment } from 'api/attachments';
import { LineItemSourceType } from 'components/organisms/invoices/credit-notes/enums';
import type {
  CreditNoteDetails,
  CreditNoteProjectWithSelection,
  CreditsByProjectWiseResponse,
  InvoicedEntity,
  LineItemsLookupWithOrder,
  LineItemsState,
  TaxDetails,
} from 'components/organisms/invoices/credit-notes/types';
import type {
  TaxCalculationMethod,
  TaxLineItemType,
  TaxSubtotalDisplayType,
} from 'components/organisms/invoices/taxes/types';

export enum APIStatus {
  Idle = 'idle',
  Loading = 'loading',
  Success = 'success',
  Error = 'error',
}

export interface CreditNoteState {
  creditNoteDetails: CreditNoteDetails;
  invoiceId?: number;
  projects: CreditNoteProjectWithSelection[];
  lineItems: Dictionary<LineItemsLookupWithOrder>;
  creditNoteSourceMappingIdsOrder: number[];
  notes?: string;
  attachments: FileAttachment[];
  currency?: Currency;
  tax: number;
  taxCodeRef?: TaxDetails;
  fields: Dictionary<any>;
  invoiceTaxCalculationMethod?: TaxCalculationMethod;
  invoiceTaxLineItemType?: TaxLineItemType;
  invoiceTaxSubtotalDisplayType?: TaxSubtotalDisplayType;
  invoiceDisplayTitle?: string;
  availableCredit?: CreditsByProjectWiseResponse;
  originalAmountByProject: Dictionary<number>;
  originalLineItemsByProject: Dictionary<
    { invoiceLineItemId: number; amount: number }[]
  >;
  maxTax: number;
  invoiceTaxRate?: number;
  invoiceTax?: number;
}

const INITIAL_DATA: CreditNoteState = {
  creditNoteDetails: {} as CreditNoteDetails,
  projects: [],
  lineItems: {},
  notes: undefined,
  attachments: [],
  currency: undefined,
  creditNoteSourceMappingIdsOrder: [],
  tax: 0,
  taxCodeRef: {
    taxCodeId: 0,
    taxCodeName: '',
    taxCodeRate: 0,
    taxComponents: [],
  },
  fields: {},
  availableCredit: undefined,
  originalAmountByProject: {},
  originalLineItemsByProject: {},
  maxTax: 0,
  invoiceTaxRate: undefined,
  invoiceTax: undefined,
};

const EMPTY_OBJECT = {};

export const creditNoteSlice = createSlice({
  initialState: INITIAL_DATA,
  name: 'creditNote',
  reducers: {
    setCreditNoteState: (
      state: CreditNoteState,
      { payload }: PayloadAction<Partial<CreditNoteState>>,
    ) => {
      return { ...state, ...payload };
    },
    setAllProjects: (state: CreditNoteState, { payload }) => {
      return {
        ...state,
        projects: payload.projects,
      };
    },
    addProjectAndCurrency: (
      state: CreditNoteState,
      {
        payload,
      }: PayloadAction<{
        currency: Currency;
        projectId: number;
      }>,
    ) => {
      const project = state.projects.find(
        (x) => x.projectId === payload.projectId,
      );

      if (!project) {
        return state;
      }

      const updatedProjects = state.projects.map((x) =>
        x.projectId === payload.projectId ? { ...x, selected: true } : x,
      );

      return {
        ...state,
        projects: updatedProjects,
        currency: payload.currency,
        creditNoteSourceMappingIdsOrder: [
          ...state.creditNoteSourceMappingIdsOrder,
          project?.creditNoteToSourceMappingId,
        ],
        lineItems: {
          ...state.lineItems,
          [payload.projectId]: {
            lineItemOrder: [],
            lineDetails: {},
            originalLineDetails: {},
          },
        },
      };
    },
    setCreditNoteBasicDetails: (
      state: CreditNoteState,
      { payload }: PayloadAction<Partial<CreditNoteState>>,
    ) => {
      return {
        ...state,
        creditNoteDetails: {
          ...state.creditNoteDetails,
          ...payload.creditNoteDetails,
        },
        fields: {
          ...state.fields,
          ...payload.fields,
        },
      };
    },
    setLineItems: (
      state: CreditNoteState,
      {
        payload: { data },
      }: PayloadAction<{ data: Dictionary<LineItemsLookupWithOrder> }>,
    ) => {
      const updatedData = Object.keys(data).reduce((acc, curr) => {
        // so that we dont remove the added line items when we go back to the line details screens
        const manuallyAddedItems = Object.keys(
          state.lineItems[curr]?.lineDetails ?? {},
        ).reduce(
          (a, x) => {
            if (state.lineItems[curr]?.lineDetails[x].isManuallyAdded) {
              a.lineDetails = {
                ...a.lineDetails,
                [x]: state.lineItems[curr]?.lineDetails[x],
              };
              a.lineItemOrder = [...a.lineItemOrder, Number(x)];
            }

            return a;
          },
          {
            lineDetails: {},
            lineItemOrder: [],
          } as LineItemsLookupWithOrder,
        );

        acc[curr] = {
          lineItemOrder: [
            ...(data[curr].lineItemOrder ?? []),
            ...manuallyAddedItems.lineItemOrder,
          ],
          lineDetails: {
            ...data[curr].lineDetails,
            ...manuallyAddedItems.lineDetails,
          },
          originalLineDetails: {
            ...(state.lineItems[curr]?.originalLineDetails || {}),
            ...(data[curr].originalLineDetails || {}),
          },
        };

        return acc;
      }, {} as Dictionary<LineItemsLookupWithOrder>);

      return {
        ...state,
        lineItems: { ...state.lineItems, ...updatedData },
      };
    },
    setSourceMappingOrder: (
      state: CreditNoteState,
      { payload: { data } }: PayloadAction<{ data: number[] }>,
    ) => {
      // done so that order if set manually doesn't change when we add or remove new projects
      const excludingRemovedIds = state.creditNoteSourceMappingIdsOrder.filter(
        (x) => data.includes(x),
      );
      // this will add all data to existing one, but making it a set will keep the order of insertion and duplicates that are later added will get removed
      const allSourceIds = [...excludingRemovedIds, ...data];
      const uniqSourceIds = [...new Set(allSourceIds)];

      return {
        ...state,
        creditNoteSourceMappingIdsOrder: uniqSourceIds,
      };
    },
    deleteLineItem: (
      state: CreditNoteState,
      {
        payload: { projectId, lineId },
      }: PayloadAction<{
        projectId: number;
        lineId: number;
        invoicedEntity: InvoicedEntity;
        sourceId: number;
      }>,
    ) => {
      const updatedLineOrder = state.lineItems[projectId].lineItemOrder.filter(
        (x) => x !== lineId,
      );

      const currentProjectLineItemsState = {
        lineDetails: { ...state.lineItems[projectId].lineDetails },
        lineItemOrder: updatedLineOrder,
        originalLineDetails: {
          ...(state.lineItems[projectId].originalLineDetails || {}),
        },
      };

      delete currentProjectLineItemsState.lineDetails[lineId];
      delete currentProjectLineItemsState.originalLineDetails[lineId];

      return {
        ...state,
        lineItems: {
          ...state.lineItems,
          [projectId]: currentProjectLineItemsState,
        },
      };
    },
    createLineItem: (
      state: CreditNoteState,
      {
        payload: { projectId, lineId, taxCodeRef, lineItemSource },
      }: PayloadAction<{
        projectId: number;
        lineId: number;
        taxCodeRef?: TaxDetails;
        lineItemSource?: LineItemSourceType;
      }>,
    ) => {
      return {
        ...state,
        lineItems: {
          ...state.lineItems,
          [projectId]: {
            lineItemOrder:
              state.lineItems[projectId].lineItemOrder.concat(lineId),
            lineDetails: {
              ...state.lineItems[projectId].lineDetails,
              [lineId]: {
                creditNoteLineItemId: lineId,
                invoicedEntity: EMPTY_OBJECT,
                description: '',
                quantity: 0,
                unitPrice: 0,
                amount: 0,
                taxCodeRef,
                projectId,
                isNew: true,
                isManuallyAdded: true,
                price: 0,
                lineItemSource,
              },
            },
            originalLineDetails: {
              ...state.lineItems[projectId].originalLineDetails,
            },
          },
        },
      };
    },
    insertLineItems: (
      state: CreditNoteState,
      {
        payload: { projectId, lineItems },
      }: PayloadAction<{
        projectId: number;
        lineItems: LineItemsState[];
      }>,
    ) => {
      const currentProjectLineItems = state.lineItems[projectId] || {
        lineItemOrder: [],
        lineDetails: {},
        originalLineDetails: {},
      };

      const newLineDetails = lineItems.reduce((acc, lineItem) => {
        acc[lineItem.creditNoteLineItemId] = lineItem;

        return acc;
      }, {} as Dictionary<LineItemsState>);

      // Store original copies for reset functionality
      const newOriginalLineDetails = lineItems.reduce((acc, lineItem) => {
        acc[lineItem.creditNoteLineItemId] = { ...lineItem };

        return acc;
      }, {} as Dictionary<LineItemsState>);

      const newLineItemIds = lineItems.map((item) => item.creditNoteLineItemId);

      return {
        ...state,
        lineItems: {
          ...state.lineItems,
          [projectId]: {
            lineItemOrder: [
              ...currentProjectLineItems.lineItemOrder,
              ...newLineItemIds,
            ],
            lineDetails: {
              ...currentProjectLineItems.lineDetails,
              ...newLineDetails,
            },
            originalLineDetails: {
              ...(currentProjectLineItems.originalLineDetails || {}),
              ...newOriginalLineDetails,
            },
          },
        },
      };
    },
    resetProjectItems: (
      state: CreditNoteState,
      { payload: { projectId } }: PayloadAction<{ projectId: number }>,
    ) => {
      const projectLineItems = state.lineItems[projectId];
      const originalLineDetails = projectLineItems.originalLineDetails || {};

      // Replace all edited items with their original unedited versions
      const resetLineDetails = { ...projectLineItems.lineDetails };

      Object.keys(originalLineDetails).forEach((lineId) => {
        if (resetLineDetails[lineId]) {
          // Restore the original unedited version
          // Exclude creditNoteLineItemId as it's a temp id that would replace the correct id
          const { creditNoteLineItemId, ...originalWithoutTempId } =
            originalLineDetails[lineId];

          resetLineDetails[lineId] = {
            ...resetLineDetails[lineId],
            ...originalWithoutTempId,
          };
        }
      });

      return {
        ...state,
        lineItems: {
          ...state.lineItems,
          [projectId]: {
            ...projectLineItems,
            lineDetails: resetLineDetails,
            lineItemOrder: projectLineItems.lineItemOrder || [],
            originalLineDetails,
          },
        },
      };
    },
    resetImportedItems: (state: CreditNoteState) => {
      const updatedLineItems = Object.keys(state.lineItems).reduce(
        (acc, projectId) => {
          const projectLineItems = state.lineItems[projectId];
          const originalLineDetails =
            projectLineItems.originalLineDetails || {};

          // Replace all edited items with their original unedited versions
          const resetLineDetails = { ...projectLineItems.lineDetails };

          Object.keys(originalLineDetails).forEach((lineId) => {
            if (resetLineDetails[lineId]) {
              // Restore the original unedited version
              // Exclude creditNoteLineItemId as it's a temp id that would replace the correct id
              const { creditNoteLineItemId, ...originalWithoutTempId } =
                originalLineDetails[lineId];

              resetLineDetails[lineId] = {
                ...resetLineDetails[lineId],
                ...originalWithoutTempId,
              };
            }
          });

          return {
            ...acc,
            [projectId]: {
              ...projectLineItems,
              lineDetails: resetLineDetails,
              lineItemOrder: projectLineItems.lineItemOrder || [],
              originalLineDetails,
            },
          };
        },
        {} as Dictionary<LineItemsLookupWithOrder>,
      );

      return {
        ...state,
        lineItems: updatedLineItems,
      };
    },
    updateLineItem: (
      state: CreditNoteState,
      {
        payload: { projectId, lineId, updates },
      }: PayloadAction<{
        projectId: number;
        lineId: number;
        updates: Partial<LineItemsState>;
      }>,
    ) => {
      return {
        ...state,
        lineItems: {
          ...state.lineItems,
          [projectId]: {
            lineItemOrder: state.lineItems[projectId].lineItemOrder,
            lineDetails: {
              ...state.lineItems[projectId].lineDetails,
              [lineId]: {
                ...state.lineItems[projectId].lineDetails[lineId],
                ...updates,
              },
            },
            originalLineDetails:
              state.lineItems[projectId].originalLineDetails || {},
          },
        },
      };
    },
    updateLineItemOrder: (
      state: CreditNoteState,
      {
        payload: { projectId, newLineItemOrder },
      }: PayloadAction<{
        projectId: number;
        newLineItemOrder: number[];
      }>,
    ) => {
      return {
        ...state,
        lineItems: {
          ...state.lineItems,
          [projectId]: {
            lineDetails: state.lineItems[projectId].lineDetails,
            lineItemOrder: newLineItemOrder,
            originalLineDetails:
              state.lineItems[projectId].originalLineDetails || {},
          },
        },
      };
    },
    setFields: (
      state: CreditNoteState,
      { payload }: PayloadAction<Dictionary<any>>,
    ) => {
      return { ...state, fields: { ...state.fields, ...payload } };
    },
    updateAllLineItemsTaxAndFinalAmount: (
      state: CreditNoteState,
      {
        payload: { updatedTax, projectId },
      }: PayloadAction<{
        updatedTax?: TaxDetails;
        projectId: number;
      }>,
    ) => {
      const updatedLineItems: Dictionary<LineItemsLookupWithOrder> = {};

      const projectLineItems = state.lineItems[projectId];
      const updatedLineDetails: Dictionary<LineItemsState> = {};

      Object.keys(projectLineItems.lineDetails).forEach((lineItemId) => {
        const lineItem = projectLineItems.lineDetails[lineItemId];
        const effectiveTax = updatedTax;
        const amount =
          (1 + (effectiveTax?.taxCodeRate ?? 0) / 100) * lineItem.amount;

        if (lineItem.lineItemSource === LineItemSourceType.Custom) {
          updatedLineDetails[lineItemId] = {
            ...lineItem,
            taxCodeRef: effectiveTax,
            amount,
          };
        } else {
          updatedLineDetails[lineItemId] = lineItem;
        }
      });

      updatedLineItems[projectId] = {
        ...projectLineItems,
        lineDetails: updatedLineDetails,
        originalLineDetails: projectLineItems.originalLineDetails || {},
      };

      return {
        ...state,
        lineItems: { ...state.lineItems, ...updatedLineItems },
        taxCodeRef: updatedTax,
      };
    },
    removeProject: (
      state: CreditNoteState,
      {
        payload: { projectId },
      }: PayloadAction<{
        projectId: number;
      }>,
    ) => {
      // Find the project to get its invoiceToSourceMappingId
      const projectToRemove = state.projects.find(
        (project) => project.projectId === projectId,
      );

      // Remove project from projects array by marking it as unselected
      const updatedProjects = state.projects.map((project) =>
        project.projectId === projectId
          ? { ...project, selected: false }
          : project,
      );

      // Create copies of dictionaries without the deleted project
      const updatedLineItems = { ...state.lineItems };

      delete updatedLineItems[projectId];

      // Filter by invoiceToSourceMappingId, not projectId
      const updatedCreditNoteSourceMappingIdsOrder =
        state.creditNoteSourceMappingIdsOrder.filter(
          (x) => x !== projectToRemove?.creditNoteToSourceMappingId,
        );

      return {
        ...state,
        projects: updatedProjects,
        lineItems: updatedLineItems,
        creditNoteSourceMappingIdsOrder: updatedCreditNoteSourceMappingIdsOrder,
      };
    },
    setAvailableCredit: (
      state: CreditNoteState,
      { payload }: PayloadAction<CreditsByProjectWiseResponse | undefined>,
    ) => {
      return { ...state, availableCredit: payload };
    },
    resetAllData: () => {
      return INITIAL_DATA;
    },
  },
});
